import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, ArrowLeft } from "lucide-react";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [params, setParams] = useSearchParams();
  const checkoutId = params.get("checkout_id");

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!checkoutId || !user) return undefined;
    let stopped = false;
    let attempts = 0;
    const check = async () => {
      try {
        const res = await api.get(`/billing/status/${encodeURIComponent(checkoutId)}`);
        if (res.data.status === "active") {
          if (!stopped) {
            await checkAuth();
            toast.success("Square confirmed your subscription. Your paid plan is active.");
            setParams({}, { replace: true });
          }
          return;
        }
      } catch (_) {
        // Square can finish creating the subscription just after redirecting the browser.
      }
      attempts += 1;
      if (!stopped && attempts < 25) setTimeout(check, 2000);
      else if (!stopped) toast.info("Square is still confirming your subscription. Refresh shortly.");
    };
    check();
    return () => { stopped = true; };
  }, [checkoutId, setParams, user, checkAuth]);

  const choose = async (tier) => {
    if (!user) { navigate("/login"); return; }
    if (tier === "free") { toast.success("You're on the Free plan."); navigate("/app"); return; }
    setBusy(tier);
    try {
      const res = await api.post(`/billing/checkout/${tier}`);
      window.location.assign(res.data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout unavailable right now.");
    } finally { setBusy(null); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your HardKnockLegal subscription at the end of the paid period?")) return;
    setBusy("cancel");
    try {
      const res = await api.post("/billing/cancel");
      toast.success(res.data.cancel_at ? `Cancellation scheduled for ${res.data.cancel_at}.` : "Cancellation scheduled with Square.");
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel the subscription.");
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-border">
        <Brand />
        <Button variant="ghost" onClick={() => navigate(user ? "/app" : "/")} data-testid="pricing-back-btn"
                className="rounded-none text-muted-foreground hover:text-primary">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="overline text-primary">Pricing</span>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3">
            Elite legal research, priced for everyone.
          </h1>
          <p className="text-muted-foreground mt-4">Start free. Upgrade for unlimited research and document drafting. Billed monthly via Square — cancel anytime.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((p) => {
            const featured = p.id === "essential";
            return (
              <div key={p.id} data-testid={`plan-${p.id}`}
                   className={`p-7 border bg-card flex flex-col ${featured ? "border-primary shadow-[0_0_40px_-12px_rgba(197,159,96,0.4)]" : "border-border/60"}`}>
                {featured && <div className="overline text-primary mb-3">Most Popular</div>}
                <h3 className="font-serif-display text-2xl font-semibold">{p.name}</h3>
                <div className="mt-3 mb-6">
                  <span className="font-serif-display text-4xl font-semibold">${p.price}</span>
                  <span className="text-muted-foreground text-sm">/{p.period}</span>
                </div>
                <ul className="space-y-3 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/90">
                      <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" strokeWidth={2} /> {f}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => choose(p.id)} disabled={busy === p.id} data-testid={`choose-${p.id}-btn`}
                        className={`w-full mt-7 h-11 rounded-none font-semibold ${featured ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-secondary text-foreground hover:bg-accent"}`}>
                  {busy === p.id ? "Redirecting…" : p.id === "free" ? "Start Free" : `Choose ${p.name}`}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-10 font-mono-tech">
          Payments processed securely by Square. HardKnockLegal.com never stores your card details.
        </p>
        {user?.subscription_status === "active" && user?.role !== "owner" && (
          <div className="text-center mt-6">
            <Button variant="outline" onClick={cancel} disabled={busy === "cancel"} className="rounded-none">
              {busy === "cancel" ? "Contacting Square…" : "Cancel subscription"}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
